// Offline document vault.
//
// Every invoice / prescription we render is stored as self-contained HTML in
// IndexedDB, so the doctor can re-open, print or "Save as PDF" it with no
// network at all. Sharing (WhatsApp / SMS) is queued while offline and sent
// once the device is back online.

import { openDB, type DBSchema, type IDBPDatabase } from "idb";

const DB_NAME = "doctorsx-doc-vault";
const DB_VERSION = 1;
const DOCS = "documents";
const SHARES = "shares";
const MAX_DOCS = 120;

export type DocType = "invoice" | "prescription";
export type ShareChannel = "whatsapp" | "sms";

export interface StoredDoc {
  id: string;
  type: DocType;
  refId?: string;
  title: string;
  filename: string;
  patientName?: string;
  phone?: string;
  html: string;
  created_at: number;
  createdOffline: boolean;
}

export interface QueuedShare {
  id: string;
  docId: string;
  channel: ShareChannel;
  phone: string;
  message: string;
  title: string;
  created_at: number;
  sent_at?: number;
}

interface VaultSchema extends DBSchema {
  documents: { key: string; value: StoredDoc };
  shares: { key: string; value: QueuedShare };
}

let dbPromise: Promise<IDBPDatabase<VaultSchema>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<VaultSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(DOCS)) db.createObjectStore(DOCS, { keyPath: "id" });
        if (!db.objectStoreNames.contains(SHARES)) db.createObjectStore(SHARES, { keyPath: "id" });
      },
    });
  }
  return dbPromise;
}

function uuid() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isOffline() {
  return typeof navigator !== "undefined" && !navigator.onLine;
}

/* ------------------------------------------------------------------ */
/* HTML preparation                                                    */
/* ------------------------------------------------------------------ */

const GOOGLE_IMPORT = /@import\s+url\(['"]https:\/\/fonts\.googleapis[^)]*\);?/g;
const GOOGLE_LINK = /<link[^>]+fonts\.(googleapis|gstatic)[^>]*>/g;

/**
 * Make a generated document renderable with zero network: remote webfont
 * requests are removed (they block printing when offline) and replaced with a
 * local Bengali-capable stack.
 */
export function prepareOfflineHtml(html: string, offline = isOffline()): string {
  if (!offline) return html;
  const fallback = `
  <style>
    /* offline: system font stack with Bengali coverage */
    body, * { font-family: 'Noto Sans Bengali', 'Nirmala UI', 'Shonar Bangla',
      'Kalpurush', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif !important; }
  </style>`;
  let out = html.replace(GOOGLE_IMPORT, "").replace(GOOGLE_LINK, "");
  out = out.includes("</head>") ? out.replace("</head>", `${fallback}</head>`) : fallback + out;
  return out;
}

/** Open a generated document in a print-ready window (works offline). */
export function openDocWindow(
  html: string,
  filename: string,
  mode: "preview" | "print" | "download" = "preview",
): boolean {
  const win = window.open("", "_blank");
  if (!win) return false;
  win.document.write(prepareOfflineHtml(html));
  win.document.close();
  try {
    win.document.title = filename;
  } catch {
    /* noop */
  }
  const triggerPrint = () => {
    try {
      win.focus();
      win.print();
    } catch {
      /* noop */
    }
  };
  if (mode === "print" || mode === "download") setTimeout(triggerPrint, 500);
  return true;
}

/* ------------------------------------------------------------------ */
/* Document store                                                      */
/* ------------------------------------------------------------------ */

type Listener = () => void;
const listeners = new Set<Listener>();
const channel =
  typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("doctorsx-doc-vault") : null;

function notify() {
  listeners.forEach((l) => l());
}
function emit() {
  notify();
  channel?.postMessage({ type: "changed" });
}
channel?.addEventListener("message", notify);

export function subscribeDocVault(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export async function saveDocument(entry: {
  type: DocType;
  refId?: string;
  title: string;
  filename: string;
  patientName?: string;
  phone?: string;
  html: string;
}): Promise<string> {
  const db = await getDb();
  const id = uuid();
  await db.put(DOCS, {
    id,
    type: entry.type,
    refId: entry.refId,
    title: entry.title,
    filename: entry.filename,
    patientName: entry.patientName,
    phone: entry.phone,
    html: prepareOfflineHtml(entry.html, true),
    created_at: Date.now(),
    createdOffline: isOffline(),
  });

  // Trim the oldest entries so the vault never grows unbounded.
  const all = await db.getAll(DOCS);
  if (all.length > MAX_DOCS) {
    const old = all.sort((a, b) => a.created_at - b.created_at).slice(0, all.length - MAX_DOCS);
    for (const doc of old) await db.delete(DOCS, doc.id);
  }
  emit();
  return id;
}

export async function listDocuments(type?: DocType): Promise<StoredDoc[]> {
  const db = await getDb();
  const all = await db.getAll(DOCS);
  return all
    .filter((d) => !type || d.type === type)
    .sort((a, b) => b.created_at - a.created_at);
}

export async function getDocument(id: string) {
  const db = await getDb();
  return db.get(DOCS, id);
}

export async function deleteDocument(id: string) {
  const db = await getDb();
  await db.delete(DOCS, id);
  emit();
}

/** Re-open a stored document, offline or not. */
export async function openStoredDocument(id: string, mode: "preview" | "print" = "preview") {
  const doc = await getDocument(id);
  if (!doc) return false;
  return openDocWindow(doc.html, doc.filename, mode);
}

/** Download a stored document as a self-contained .html file (offline safe). */
export async function downloadStoredDocument(id: string) {
  const doc = await getDocument(id);
  if (!doc) return false;
  const blob = new Blob([doc.html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${doc.filename}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return true;
}

/* ------------------------------------------------------------------ */
/* Share queue                                                         */
/* ------------------------------------------------------------------ */

function normalizePhone(phone: string) {
  const digits = phone.replace(/[^\d]/g, "");
  if (digits.startsWith("880")) return digits;
  if (digits.startsWith("0")) return `88${digits}`;
  return digits;
}

function shareUrl(share: QueuedShare) {
  const text = encodeURIComponent(share.message);
  if (share.channel === "sms") return `sms:${share.phone}?&body=${text}`;
  return `https://wa.me/${normalizePhone(share.phone)}?text=${text}`;
}

export async function listQueuedShares(): Promise<QueuedShare[]> {
  const db = await getDb();
  const all = await db.getAll(SHARES);
  return all.filter((s) => !s.sent_at).sort((a, b) => a.created_at - b.created_at);
}

export async function countQueuedShares() {
  return (await listQueuedShares()).length;
}

export async function queueShare(entry: {
  docId: string;
  channel: ShareChannel;
  phone: string;
  message: string;
  title: string;
}): Promise<string> {
  const db = await getDb();
  const id = uuid();
  await db.put(SHARES, { id, ...entry, created_at: Date.now() });
  emit();
  return id;
}

export async function cancelShare(id: string) {
  const db = await getDb();
  await db.delete(SHARES, id);
  emit();
}

/**
 * Send a queued share now. Opening WhatsApp/SMS needs a user gesture, so this
 * is always triggered from a button — never automatically.
 */
export async function sendQueuedShare(id: string): Promise<boolean> {
  const db = await getDb();
  const share = await db.get(SHARES, id);
  if (!share) return false;
  if (isOffline()) return false;
  window.open(shareUrl(share), "_blank");
  await db.delete(SHARES, id);
  emit();
  return true;
}

/**
 * Share a document immediately when online, otherwise queue it for later.
 * Returns whether the share was deferred.
 */
export async function shareDocument(entry: {
  docId: string;
  channel: ShareChannel;
  phone: string;
  message: string;
  title: string;
}): Promise<{ queued: boolean }> {
  if (isOffline()) {
    await queueShare(entry);
    return { queued: true };
  }
  window.open(
    shareUrl({ ...entry, id: "", created_at: Date.now() } as QueuedShare),
    "_blank",
  );
  return { queued: false };
}

let initialized = false;

/** Warn once, on reconnect, that documents are waiting to be sent. */
export function initDocShareOutbox(onPending?: (count: number) => void) {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  const check = async () => {
    const count = await countQueuedShares();
    if (count > 0) onPending?.(count);
  };
  window.addEventListener("online", () => void check());
  void check();
}
